/*
 * OBX Airwaves — Outer Banks Tides (NOAA CO-OPS predictions)
 * Source: https://obxairwaves.com/weatherdata/tides.json
 * Registered alongside existing Marine slide using OBX custom display loader.
 * Uses UTC prediction instants, displays Eastern local time including DST.
 */
import STATUS from '../../modules/status.mjs';
import WeatherDisplay from '../../modules/weatherdisplay.mjs';
import { registerDisplay } from '../../modules/navigation.mjs';

const DATA_URL = 'https://obxairwaves.com/weatherdata/tides.json';
const MAX_FILE_AGE_MS = 3 * 24 * 60 * 60 * 1000;
const FETCH_TIMEOUT_MS = 12000;
const DAY_MS = 24 * 60 * 60 * 1000;

const STATIONS = [
    { id: '8651370', name: 'DUCK' },
    { id: '8652587', name: 'OREGON INLET' },
    { id: '8654400', name: 'CAPE HATTERAS PIER' },
    { id: 'TEC2793', name: 'OCRACOKE INLET' },
];

function tideLabel(instantMs) {
    // UTC instants from NOAA/PHP mean daylight-saving boundaries remain unambiguous.
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York', month: 'short', day: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    }).formatToParts(new Date(instantMs));
    const part = (type) => parts.find((item) => item.type === type)?.value ?? '';
    return { time: `${part('hour')}:${part('minute')} ${part('dayPeriod').toUpperCase()}`, date: `${part('month').toUpperCase()} ${part('day')}` };
}

function pickNext(predictions, nowMs) {
    const next = { H: null, L: null };
    if (!Array.isArray(predictions)) return next;
    for (const event of predictions) {
        if (!event || (event.type !== 'H' && event.type !== 'L')) continue;
        // Strict Z-suffixed UTC ISO strings only. Never interpret local times as UTC.
        if (typeof event.time_utc !== 'string' || !/^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\dZ$/.test(event.time_utc)) continue;
        const ms = Date.parse(event.time_utc);
        if (!Number.isFinite(ms) || ms <= nowMs || ms > nowMs + 8 * DAY_MS) continue;
        if (typeof event.height_ft !== 'number' || !Number.isFinite(event.height_ft) || event.height_ft < -30 || event.height_ft > 40) continue;
        if (!next[event.type] || ms < next[event.type].ms) {
            next[event.type] = { ms, height: event.height_ft };
        }
    }
    return next;
}

function renderPrediction(cell, event) {
    if (!cell) return;
    const time = cell.querySelector('.obx-tides-time');
    const height = cell.querySelector('.obx-tides-height');
    const label = event ? tideLabel(event.ms) : null;
    if (time) time.textContent = label ? label.time : '--';
    if (height) height.textContent = label ? `${label.date} • ${event.height.toFixed(2)} FT` : 'NO DATA';
}

class ObxTides extends WeatherDisplay {
    constructor() {
        // ID 13: follows existing OBX Marine slide ID 12.
        // Disabled by default; enable via checkbox or ?obx-tides=true.
        super(13, 'obx-tides', 'OBX Tide Predictions', false);
        this.refreshTime = 30 * 60 * 1000;
        this.timing.totalScreens = 1;
        this.timing.baseDelay = 11000;
        this.timing.delay = 1;
    }

    async getData(weatherParameters, refresh = false) {
        if (!super.getData(weatherParameters, refresh)) return;
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
        try {
            const response = await fetch(`${DATA_URL}?nocache=${Date.now()}`, {
                mode: 'cors', cache: 'no-store', signal: controller.signal,
            });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const doc = await response.json();
            if (doc?.schema_version !== 1 || doc.datum !== 'MLLW' || doc.height_units !== 'feet' || doc.time_zone !== 'UTC'
                || !Array.isArray(doc.stations)) throw new Error('Unexpected tides.json schema');
            const retrievedMs = Date.parse(doc.retrieved_at_utc);
            const age = Date.now() - retrievedMs;
            if (!Number.isFinite(retrievedMs) || age < -10 * 60000 || age > MAX_FILE_AGE_MS) {
                throw new Error('Tide data update is stale or future-dated');
            }
            const seen = new Set();
            const stationData = new Map();
            for (const row of doc.stations) {
                if (!row || !STATIONS.some((station) => station.id === row.station_id) || seen.has(row.station_id)) continue;
                seen.add(row.station_id);
                const lastMs = Date.parse(row.last_success_utc);
                if (!['current', 'cached'].includes(row.status) || !Number.isFinite(lastMs)
                    || lastMs > Date.now() + 10 * 60000 || Date.now() - lastMs > MAX_FILE_AGE_MS) continue;
                const next = pickNext(row.predictions, Date.now());
                if (next.H && next.L) stationData.set(row.station_id, row.predictions);
            }
            if (stationData.size === 0) throw new Error('No usable upcoming high/low predictions');
            this.data = stationData;
            this.timing.totalScreens = 1;
            this.calcNavTiming();
            this.getDataCallback();
            this.setStatus(STATUS.loaded);
        } catch (error) {
            this.data = undefined;
            this.timing.totalScreens = 0;
            this.calcNavTiming();
            this.getDataCallback();
            this.setStatus(STATUS.noData);
            console.warn('OBX Tides slide skipped:', error?.message ?? error);
        } finally {
            clearTimeout(timeout);
        }
    }

    drawCanvas() {
        super.drawCanvas();
        if (!this.elem || !this.data) { this.finishDraw(); return; }
        const now = Date.now();
        for (const station of STATIONS) {
            const element = this.elem.querySelector(`[data-tide-station="${station.id}"]`);
            if (!element) continue;
            const next = pickNext(this.data.get(station.id), now);
            renderPrediction(element.querySelector('.obx-tides-high'), next.H);
            renderPrediction(element.querySelector('.obx-tides-low'), next.L);
            element.classList.toggle('obx-tides-unavailable', !(next.H && next.L));
        }
        this.finishDraw();
    }
}

registerDisplay(new ObxTides());
